// Address DGC balance — plain language for explorers / genesis pools
import { useSuiClientQuery } from '@mysten/dapp-kit';
import { Link } from 'react-router-dom';

import { useGenesisPoolForAddress } from '~/hooks/useGenesisPoolForAddress';
import { CPC_SYMBOL, DGC_TYPE_ARG } from '~/utils/cpcCoin';
import { CpcPoolVestingPanel } from '~/components/cpc/CpcPoolVestingPanel';

function formatMist(mist: string | number | bigint | undefined): string {
	if (mist === undefined || mist === null) return '0';
	const m = BigInt(mist);
	const whole = m / 1_000_000_000n;
	const frac = m % 1_000_000_000n;
	if (frac === 0n) return whole.toLocaleString('zh-CN');
	const fracStr = frac.toString().padStart(9, '0').replace(/0+$/, '');
	return `${whole.toLocaleString('zh-CN')}.${fracStr}`;
}

export function CpcAddressBalance({ address }: { address: string }) {
	const { pool } = useGenesisPoolForAddress(address);
	const { data, isPending, isError } = useSuiClientQuery('getBalance', {
		owner: address,
		coinType: DGC_TYPE_ARG,
	});

	const addressBal = data?.fundsInAddressBalance ?? '0';
	const total = data?.totalBalance ?? '0';
	const inCoins =
		data && BigInt(total) > BigInt(addressBal)
			? (BigInt(total) - BigInt(addressBal)).toString()
			: '0';

	return (
		<div className="mb-4 space-y-3">
			{pool ? (
				<div className="rounded-xl border border-[#38BDF8]/35 bg-[#38BDF8]/10 px-4 py-3 text-[#e2e8f0]">
					<p className="text-xs font-medium uppercase tracking-wide text-[#7dd3fc]">创世分配池</p>
					<p className="mt-1 text-lg font-semibold text-white">{pool.name}</p>
					<p className="mt-1 text-sm text-[#cbd5e1]">
						创世额度 {pool.amount.toLocaleString('zh-CN')} DGC（{pool.pct}%） · {pool.vestingText}
					</p>
					{pool.vestingDetail ? (
						<p className="mt-1 text-xs leading-relaxed text-[#94a3b8]">{pool.vestingDetail}</p>
					) : null}
					{pool.note ? <p className="mt-1 text-xs text-[#64748b]">{pool.note}</p> : null}
					<Link to="/genesis" className="mt-2 inline-block text-xs font-medium text-[#38BDF8] hover:underline">
						查看完整创世分配表 →
					</Link>
				</div>
			) : null}

			{/* Keep vesting claim UI even when getBalance fails (do not gate on balance query). */}
			{pool?.vesting === 'linear_10y' ? (
				<CpcPoolVestingPanel address={address} poolName={pool.name} />
			) : null}

			{isPending ? (
				<div className="rounded-xl border border-white/15 bg-white/10 p-4">
					<p className="text-sm text-[#94a3b8]">正在读取 DGC 余额…</p>
				</div>
			) : isError || !data ? (
				<div className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-[#fecaca]">
					<p className="text-sm">DGC 余额读取失败（RPC）。线性释放面板仍可在上方操作。</p>
				</div>
			) : (
				<div className="rounded-xl border border-white/15 bg-white/10 p-4 text-[#e2e8f0]">
					<p className="text-xs font-medium text-[#94a3b8]">DGC 总余额</p>
					<p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-white">
						{formatMist(total)}{' '}
						<span className="text-lg font-semibold text-[#aeb9d0]">{CPC_SYMBOL}</span>
					</p>

					<div className="mt-4 grid gap-2 sm:grid-cols-2">
						<div className="rounded-lg border border-white/10 bg-black/25 px-3 py-2.5">
							<p className="text-xs text-[#94a3b8]">可用余额</p>
							<p className="mt-0.5 text-base font-semibold tabular-nums text-white">
								{formatMist(addressBal)} {CPC_SYMBOL}
							</p>
							<p className="mt-1 text-[11px] leading-snug text-[#64748b]">可直接付 Gas、转账、交易</p>
						</div>
						<div className="rounded-lg border border-white/10 bg-black/25 px-3 py-2.5">
							<p className="text-xs text-[#94a3b8]">代币对象中</p>
							<p className="mt-0.5 text-base font-semibold tabular-nums text-white">
								{formatMist(inCoins)} {CPC_SYMBOL}
								{data.coinObjectCount != null ? (
									<span className="ml-1 text-xs font-normal text-[#94a3b8]">
										· {data.coinObjectCount} 个对象
									</span>
								) : null}
							</p>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
